
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BookOpen, Users, Calculator, PenTool, Puzzle, Gamepad2 } from "lucide-react";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import Seo from "@/components/seo/Seo";

const SubjectSelection = () => {
  const { grade } = useParams();
  const navigate = useNavigate();
  const [subjects, setSubjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSubjects();
  }, [grade]);

  const fetchSubjects = async () => {
    try {
      const { data, error } = await supabase
        .from('subjects')
        .select('*')
        .eq('grade', grade)
        .order('name');

      if (error) {
        console.error('Error fetching subjects:', error);
        return;
      }

      setSubjects(data || []);
    } catch (error) {
      console.error('Error fetching subjects:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSubjectIcon = (subjectName: string) => {
    const name = subjectName.toLowerCase();
    if (name.includes('math') || name.includes('numeracy')) {
      return <Calculator className="h-8 w-8" />;
    } else if (name.includes('reading') || name.includes('literacy')) {
      return <BookOpen className="h-8 w-8" />;
    } else if (name.includes('writing')) {
      return <PenTool className="h-8 w-8" />;
    }
    return <Users className="h-8 w-8" />;
  };

  const getSubjectColor = (index: number) => {
    const colors = [
      'from-blue-500 to-blue-600',
      'from-green-500 to-green-600', 
      'from-purple-500 to-purple-600',
      'from-orange-500 to-orange-600',
      'from-pink-500 to-pink-600'
    ];
    return colors[index % colors.length];
  };

  // Activity options for after subject selection
  const activities = [
    {
      icon: PenTool,
      title: 'Writing Practice',
      description: 'Improve your writing skills',
      path: `/writing/${grade}`,
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: Puzzle,
      title: 'Worksheets',
      description: 'Interactive learning activities',
      path: `/worksheets/${grade}`,
      color: 'from-purple-500 to-violet-500'
    },
    {
      icon: Gamepad2,
      title: 'Games',
      description: 'Learn through fun games',
      path: `/games/${grade}`,
      color: 'from-orange-500 to-red-500'
    }
  ];

  const handleActivitySelect = (subject: any, activityType: string) => {
    const subjectPath = subject.name.toLowerCase().replace(/\s+/g, '-');
    
    switch(activityType) {
      case 'tests':
        navigate(`/grade/${grade}/subject/${subjectPath}`);
        break;
      case 'writing':
        navigate(`/writing/${grade}`);
        break;
      case 'worksheets':
        navigate(`/worksheets/${grade}`);
        break;
      case 'games':
        navigate(`/games/${grade}`);
        break;
      default:
        navigate(`/grade/${grade}/subject/${subjectPath}`);
    }
  };

  const handleQuickActivity = (path: string) => {
    navigate(path);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-xl">Loading subjects...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <Seo
        title={`Grade ${grade} Practice Tests, Worksheets & Writing`}
        description={`Free Grade ${grade} practice — AI-generated tests, worksheets, writing prompts, and learning games aligned to the curriculum.`}
        canonicalPath={`/grade/${grade}`}
      />
      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Navigation Header */}
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-3xl font-bold text-gray-800">
              Choose Your Subject - Grade {grade}
            </h1>
          </div>

          {/* Quick Access Activities */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Quick Access Activities</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {activities.map((activity, index) => (
                <motion.div
                  key={activity.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className="border-2 border-gray-200 hover:border-blue-400 transition-all duration-300 cursor-pointer hover:shadow-lg">
                    <CardContent 
                      className="p-6 text-center"
                      onClick={() => handleQuickActivity(activity.path)}
                    >
                      <div className={`w-16 h-16 rounded-full bg-gradient-to-r ${activity.color} flex items-center justify-center mx-auto mb-4 shadow-lg`}>
                        <activity.icon className="h-8 w-8 text-white" />
                      </div>
                      <h3 className="font-bold text-gray-800 text-lg mb-2">{activity.title}</h3>
                      <p className="text-sm text-gray-600 mb-4">{activity.description}</p>
                      <Button 
                        className={`w-full bg-gradient-to-r ${activity.color} hover:opacity-90 text-white`}
                        size="sm"
                      >
                        Start Now
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Subject-specific Tests */}
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Subject-Specific Tests</h2>
            {subjects.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {subjects.map((subject, index) => (
                  <motion.div
                    key={subject.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <Card className="border-2 border-gray-200 hover:border-blue-400 transition-all duration-300 cursor-pointer h-full hover:shadow-lg">
                      <CardHeader className="text-center">
                        <div className={`mx-auto w-20 h-20 rounded-full bg-gradient-to-r ${getSubjectColor(index)} flex items-center justify-center text-white mb-4 shadow-lg`}>
                          {getSubjectIcon(subject.name)}
                        </div>
                        <CardTitle className="text-xl text-gray-800">{subject.name}</CardTitle>
                        {subject.description && (
                          <p className="text-gray-600 text-sm">{subject.description}</p>
                        )}
                      </CardHeader>
                      
                      <CardContent>
                        <Button
                          className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                          onClick={() => handleActivitySelect(subject, 'tests')}
                        >
                          Start Testing
                        </Button>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">📚</div>
                <p className="text-xl text-gray-600 mb-4">No subjects available for Grade {grade}</p>
                <p className="text-gray-500 mb-6">But you can still access other learning activities!</p>
                <div className="flex gap-4 justify-center">
                  <Button
                    onClick={() => navigate('/dashboard')}
                  >
                    Go to Dashboard
                  </Button>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default SubjectSelection;
